import React, { useState } from 'react';
import { Sparkles, Layout, Copy, Check, Download, Clock, User, Activity, AlertCircle, Building2, History, MessageSquare, FileText } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import AdvisingDashboard from '../home/AdvisingDiagnostic/AdvisingDashboard';
import { formatTimestamp, formatDuration } from '@/utils/dateUtils';
import './styles.scss';

const ExecutionDetail = ({ executionData }) => {
    const [copied, setCopied] = useState(false);

    if (!executionData) return null;

    const response = executionData.response || {};
    const result = response.result || {};
    const linqResult = result.finalResult || result.outputData || result;

    const tryParse = (str) => {
        if (typeof str !== 'string') return str;
        try {
            // Clean up markdown code blocks if present
            const cleanStr = str.replace(/```json\n?|\n?```/g, '').trim();
            return JSON.parse(cleanStr);
        } catch (e) {
            try {
                return JSON.parse(str);
            } catch (err) {
                return null;
            }
        }
    };

    // Helper to extract question/transcript from various possible paths
    const getInquiryContext = () => {
        const paths = [
            executionData.request,
            executionData.input_data,
            executionData.inputData,
            executionData.request?.query,
            executionData.request?.query?.params
        ];

        let question = null;
        let transcript = null;

        for (const p of paths) {
            if (!p) continue;
            if (!question && p.question) question = p.question;
            if (!transcript && p.transcript) transcript = p.transcript;
        }

        // Final fallback for question: check 'context' if it's a simple string
        if (!question && executionData.request?.context && typeof executionData.request.context === 'string' && executionData.request.context.length < 500) {
            question = executionData.request.context;
        }

        return { question, transcript };
    };

    const { question, transcript } = getInquiryContext();

    const findDeep = (obj, key) => {
        if (!obj || typeof obj !== 'object') return null;
        if (obj[key]) return obj[key];
        for (const k in obj) {
            const found = findDeep(obj[k], key);
            if (found) return found;
        }
        return null;
    };

    let parsed = tryParse(linqResult);
    const dashboardData = findDeep(parsed || linqResult, 'dashboard_data') || findDeep(parsed || linqResult, 'dashboardData');
    const masterReport = findDeep(parsed || linqResult, 'final_report') || findDeep(parsed || linqResult, 'finalReport');
    const isMasterReport = !!masterReport;

    const finalAdvice = masterReport || (typeof linqResult === 'string' ? linqResult : JSON.stringify(linqResult, null, 2));

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };


    return (
        <div className="execution-detail-container">
            <div className="execution-meta-grid">
                <div className="meta-item">
                    <Activity size={16} />
                    <span className="label">Status:</span>
                    <span className={`value status-${executionData.status?.toLowerCase()}`}>
                        {executionData.status}
                    </span>
                </div>
                <div className="meta-item">
                    <User size={16} />
                    <span className="label">Executed By:</span>
                    <span className="value">{executionData.executedBy || 'Anonymous'}</span>
                </div>
                <div className="meta-item">
                    <Clock size={16} />
                    <span className="label">Time:</span>
                    <span className="value">{formatTimestamp(executionData.executedAt)}</span>
                </div>
                <div className="meta-item">
                    <History size={16} />
                    <span className="label">Duration:</span>
                    <span className="value">{formatDuration(executionData.durationMs)}</span>
                </div>
                <div className="meta-item">
                    <Building2 size={16} />
                    <span className="label">Institution:</span>
                    <span className="value">
                        {executionData.institution || 
                         executionData.resolvedInstitution ||
                         executionData.request?.institution || 
                         executionData.request?.query?.params?.institution || 
                         executionData.input_data?.institution || 
                         executionData.inputData?.institution || 
                         'N/A'}
                    </span>
                </div>
                <div className="meta-item">
                    <Layout size={16} />
                    <span className="label">Agent Task:</span>
                    <span className="value">
                        {((executionData.agentTaskName && executionData.agentTaskName !== 'N/A') ? executionData.agentTaskName : null) || 
                         ((executionData.taskName && executionData.taskName !== 'N/A') ? executionData.taskName : null) || 
                         ((executionData.agentTaskId && executionData.agentTaskId !== 'N/A') ? executionData.agentTaskId : null) || 
                         ((executionData.taskId && executionData.taskId !== 'N/A') ? executionData.taskId : null) || 
                         (executionData.agentName ? `${executionData.agentName} Task` : 'AI Pathway Analysis')}
                    </span>
                </div>
            </div>
            
            {executionData.status === 'FAILED' && (
                <div className="execution-error-banner">
                    <AlertCircle size={20} />
                    <div className="error-text">
                        <strong>Execution Failed:</strong> {String(linqResult)}
                    </div>
                </div>
            )}

            {dashboardData ? (
                <div className="diagnostic-results dashboard-mode">
                    <div className="results-header">
                        <div className="header-title">
                            <Layout className="icon-sparkle" size={20} />
                            <h3>Intelligence Dashboard</h3>
                        </div>
                        <div className="header-actions">
                            <button className="action-btn" onClick={() => handleCopy(finalAdvice)}>
                                {copied ? <Check size={18} color="#10b981" /> : <Copy size={18} />}
                                <span>Copy Summary</span>
                            </button>
                        </div>
                    </div>
                    <AdvisingDashboard 
                        data={parsed || linqResult} 
                        question={question}
                        transcript={transcript}
                    />
                </div>
            ) : (
                <div className="diagnostic-results">
                    <div className="results-header">
                        <div className="header-title">
                            <Sparkles className="icon-sparkle" size={20} />
                            <h3>Analysis Result</h3>
                        </div>
                        <div className="header-actions">
                            <button className="action-btn" onClick={() => handleCopy(finalAdvice)}>
                                {copied ? <Check size={18} color="#10b981" /> : <Copy size={18} />}
                                <span>Copy</span>
                            </button>
                        </div>
                    </div>

                    <AdvisingDashboard 
                        data={parsed || linqResult} 
                        question={question}
                        transcript={transcript}
                    />
                </div>
            )}
        </div>
    );
};

export default ExecutionDetail;
